

import React from 'react';
import cn from 'classnames';
import parse from 'html-react-parser';

import styles from './trip-notification-page.module.css';
import { DeliveryMethod, TripNotification } from '../types/tripNotification';

import Alert from '../components/alert';
import TabularGridForKeyElementDisplay, {
  RowCellData,
} from '../components/grids/TabularGridForKeyElementDisplay';

import { toTitleCase, snakeToTitleCase } from '../lib/strings';
import { toFullDateTimeDisplay } from '../lib/dateTime';

const NOT_APPLICABLE = 'N/A';

const getPresentableState = (state: string) => {
  switch (state.toLocaleUpperCase()) {
    case 'READY':
    case 'DELIVERY_PENDING_BY_VENDOR':
      return 'Pending';

    case 'SENT':
    case 'RECEIVED_BY_VENDOR':
      return 'Sent';

    case 'FAILED':
    case 'EXPIRED':
    case 'DELIVERY_FAILED_BY_VENDOR':
    case 'DELIVERY_EXPIRED_BY_VENDOR':
    case 'TEMPLATE_RENDER_FAILED':
    case 'SEND_REQUEST_REJECTED':
    case 'REJECTED_BY_VENDOR':
    case 'MAX_PRICE_EXCEEDED':
      return 'Failed';

    case 'DELIVERED_BY_VENDOR':
      return 'Delivered';

    default:
      return snakeToTitleCase(state);
  }
};

interface NotificationProps {
  isEmail: boolean;
  tripNotification: TripNotification;
}

const Notification: React.FC<NotificationProps> = ({ isEmail, tripNotification }) => {
  const messageType =
    DeliveryMethod.EMAIL === tripNotification.deliveryMethod
      ? toTitleCase(tripNotification.deliveryMethod)
      : tripNotification.deliveryMethod;

  const commonRowCellData: RowCellData[] = [
    { label: 'Message Type:', value: messageType },
    {
      label: 'Message State:',
      value: getPresentableState(tripNotification.state),
    },
    {
      label: 'Recipient:',
      value: tripNotification.recipient,
    },
    {
      label: 'Sent Time:',
      value: tripNotification.sentTime
        ? toFullDateTimeDisplay(tripNotification.sentTime)
        : NOT_APPLICABLE,
    },
  ];

  let rowCellData: RowCellData[] = [...commonRowCellData];

  if (isEmail) {
    const emailRowCellData: RowCellData[] = [
      {
        label: 'Sender Alias:',
        value: tripNotification.senderAlias || NOT_APPLICABLE,
      },
      {
        label: 'Sender:',
        value: tripNotification.sender || NOT_APPLICABLE,
      },
    ];
    rowCellData = [...rowCellData, ...emailRowCellData];
  }

  const renderedBody =
    isEmail && tripNotification.renderedBody
      ? parse(tripNotification.renderedBody)
      : tripNotification.renderedBody || NOT_APPLICABLE;

  return (
    <>
      <TabularGridForKeyElementDisplay data={rowCellData} />

      {isEmail && (
        <div className={cn(styles.rowGap)}>
          <div className={cn(styles.label)}>Subject: </div>
          <div>{tripNotification.renderedSubject || NOT_APPLICABLE}</div>
        </div>
      )}

      <div>
        <div className={cn(styles.label)}>Message Body: </div>
        <div>{renderedBody}</div>
      </div>
    </>
  );
};

interface TripNotificationPageProps {
  tripNotification: TripNotification | null;
  hasGetTripNotificationError: boolean;
}

export const TripNotificationPage: React.FC<TripNotificationPageProps> = ({
  tripNotification,
  hasGetTripNotificationError,
}) => {
  return (
    <div className={cn(styles.root, 'container-fluid')}>
      {hasGetTripNotificationError && (
        <Alert
          type="danger"
          message="Unable to load the trip alert message. This may have occured because you are trying to view an old or expired trip alert message."
        />
      )}
      {tripNotification && (
        <Notification
          isEmail={DeliveryMethod.EMAIL === tripNotification.deliveryMethod}
          tripNotification={tripNotification}
        />
      )}
    </div>
  );
};
